import React from 'react';
import { colors } from '@insurancemenu/imenu-components-lib';
import { BriefcaseMedical, Reload, Star, Tools } from '@insurancemenu/imenu-components-lib/dist/icons';
import { IMBaseColors } from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { AccessTag } from 'components/common/general/AccessTag';
import { ProposalExperience } from 'store/modules/initiate-quote/types';
import {
  DescriptionBlockStyled,
  IconWrapperStyled,
  DescriptionBlockTitleStyled,
  DescriptionBlockDescriptionStyled,
  QuoteExperienceOptionStyled,
  DisabledReasonBlockStyled,
  DisabledReasonTextStyled,
  DISABLED_BACKGROUND_ALPHA,
  InfoButtonFilledStyled,
} from './styles';

interface OptionContent {
  label: string;
  description: string;
  Icon: React.ComponentType<{ width: string; height: string; color: string }>;
  iconSize: string;
  color: IMBaseColors;
}

const QUOTE_EXPERIENCE_OPTION_MAP: Record<ProposalExperience, OptionContent> = {
  SHELF: {
    label: 'Create a Shelf Proposal',
    description: 'Build a proposal using predefined plans and standard options for a faster setup',
    Icon: BriefcaseMedical,
    iconSize: '3rem',
    color: 'green',
  },
  RENEWAL_SHELF: {
    label: 'Create a Renewal Shelf Proposal',
    description: 'Build a proposal using predefined plans and standard options for a faster setup',
    Icon: Reload,
    iconSize: '3rem',
    color: 'orange',
  },
  RENEWAL_CUSTOM: {
    label: 'Create a Renewal Custom Proposal',
    description: `Build a customized proposal by requesting plan options tailored to your group's specific needs`,
    Icon: Tools,
    iconSize: '3rem',
    color: 'purple',
  },
  FAVORITE: {
    label: 'Reuse a Favorite Proposal',
    description: 'Quickly create a new proposal from one of your saved favorite proposals and customize it as needed',
    Icon: Star,
    iconSize: '3.4rem',
    color: 'yellow',
  },
  CUSTOM: {
    label: 'Initiate a Custom Proposal',
    description: "Build a customized proposal by requesting plan options tailored to your group's specific needs",
    Icon: Tools,
    iconSize: '3rem',
    color: 'purple',
  },
};

const QuoteExperienceOption: React.FC<{
  experience: ProposalExperience;
  disabledReason?: string | null;
  accessLimitation?: string;
  children?: React.ReactNode;
}> = ({ experience, disabledReason, accessLimitation, children }) => {
  const { label, description, Icon, iconSize, color } = QUOTE_EXPERIENCE_OPTION_MAP[experience];
  const isDisabled = Boolean(disabledReason);
  const experienceColor = colors[color];
  return (
    <>
      <QuoteExperienceOptionStyled>
        <IconWrapperStyled
          $accentColor={isDisabled ? `${colors.neutral[200]}${DISABLED_BACKGROUND_ALPHA}` : experienceColor[100]}
        >
          <Icon width={iconSize} height={iconSize} color={isDisabled ? colors.neutral[500] : experienceColor[600]} />
        </IconWrapperStyled>
        <DescriptionBlockStyled>
          <DescriptionBlockTitleStyled>
            {label}
            {accessLimitation && <AccessTag size="small" accessLimitation={accessLimitation} />}
          </DescriptionBlockTitleStyled>
          <DescriptionBlockDescriptionStyled>{description}</DescriptionBlockDescriptionStyled>
          {children}
        </DescriptionBlockStyled>
      </QuoteExperienceOptionStyled>
      {disabledReason && (
        <DisabledReasonBlockStyled>
          <InfoButtonFilledStyled width="2rem" height="2rem" color={colors.indigo[600]} />
          <DisabledReasonTextStyled>{disabledReason}</DisabledReasonTextStyled>
        </DisabledReasonBlockStyled>
      )}
    </>
  );
};

export default QuoteExperienceOption;
